import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Store, Phone, MapPin, Mail, Globe, FileText, Layers, CheckCircle, MessageCircle, Smartphone, Shield, ChevronDown, ChevronUp, TestTube, Send, Bell, CreditCard, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../layouts/AdminLayout';
import { storesApi, waApi, uploadsApi } from '../../services/api';
import { formatApiError, Spinner } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { BUSINESS_TYPES } from '../../config/businessTypes';

export default function SettingsPage() {
  const { t } = useLanguage();
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [activeTab, setActiveTab] = useState('umum');

  const { isLoading } = useQuery({
    queryKey: ['store-current'],
    queryFn: () => storesApi.getCurrent().then(r => r.data),
    onSuccess: (data) => { if (!form) setForm(data); },
  });

  React.useEffect(() => {
    if (!form && user?.store) setForm(user.store);
  }, [user]);

  const mutation = useMutation({
    mutationFn: d => storesApi.update(d),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      await refreshUser();
      toast.success(t('Pengaturan disimpan!', 'Settings saved!'));
    },
    onError: err => toast.error(formatApiError(err)),
  });

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setStoreValue = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const setWa = k => v => setForm(f => ({ ...f, wa_settings: { ...(f.wa_settings || {}), [k]: v } }));
  const setWaTrigger = k => v => setForm(f => ({
    ...f,
    wa_settings: {
      ...(f.wa_settings || {}),
      notification_triggers: { ...((f.wa_settings || {}).notification_triggers || {}), [k]: v }
    }
  }));

  // WA test state
  const [testTarget, setTestTarget] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState('status_update');

  const testMutation = useMutation({
    mutationFn: () => waApi.testToken(
      form?.wa_settings?.integration_type === 'pribadi' ? form?.wa_settings?.custom_token : null,
      testTarget || form?.phone
    ),
    onSuccess: (res) => {
      const f = res.data?.response;
      if (f?.status === false) toast.error(`Gagal: ${f?.reason || 'Token salah / device offline'}`);
      else toast.success('WA test berhasil terkirim!');
    },
    onError: err => toast.error(formatApiError(err)),
  });

  const wa = form?.wa_settings || {};
  const triggers = wa.notification_triggers || {};

  // ── Payment Methods helpers ─────────────────────────────────────────────
  const [qrisUploading, setQrisUploading] = useState(false);
  const pm = form?.payment_methods || {};
  const setPm = (key, field, value) => setForm(f => ({
    ...f,
    payment_methods: { ...(f.payment_methods || {}), [key]: { ...((f.payment_methods || {})[key] || {}), [field]: value } }
  }));
  const handleQrisUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setQrisUploading(true);
    try {
      const res = await uploadsApi.uploadQris(file);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setPm('qris', 'qris_image_url', fullUrl);
      toast.success('Gambar QRIS berhasil diupload!');
    } catch {
      toast.error('Gagal upload QRIS. Coba lagi.');
    } finally {
      setQrisUploading(false);
    }
  };
  const PAYMENT_METHODS = [
    { key: 'bca', label: 'BCA', subtitle: 'Bank Central Asia', type: 'bank', color: 'bg-blue-100 text-blue-700' },
    { key: 'bni', label: 'BNI', subtitle: 'Bank Negara Indonesia', type: 'bank', color: 'bg-orange-100 text-orange-700' },
    { key: 'bri', label: 'BRI', subtitle: 'Bank Rakyat Indonesia', type: 'bank', color: 'bg-sky-100 text-sky-700' },
    { key: 'mandiri', label: 'Mandiri', subtitle: 'Bank Mandiri', type: 'bank', color: 'bg-yellow-100 text-yellow-700' },
    { key: 'gopay', label: 'GoPay', subtitle: 'Dompet Digital', type: 'ewallet', color: 'bg-teal-100 text-teal-700' },
    { key: 'dana', label: 'DANA', subtitle: 'Dompet Digital', type: 'ewallet', color: 'bg-blue-100 text-blue-700' },
    { key: 'shopee_pay', label: 'ShopeePay', subtitle: 'Dompet Digital', type: 'ewallet', color: 'bg-orange-100 text-orange-700' },
    { key: 'qris', label: 'QRIS', subtitle: 'QR Code Payment', type: 'qris', color: 'bg-purple-100 text-purple-700' },
  ];
  const TEMPLATE_KEYS = [
    { key: 'order_created', label: 'Konfirmasi Order Baru', desc: 'Dikirim saat order pertama kali dibuat' },
    { key: 'status_update', label: 'Update Status', desc: 'Saat status berubah (Diproses / Selesai)' },
    { key: 'order_ready_paid', label: 'Siap Diambil — Lunas', desc: 'Saat status menjadi Siap Diambil dan sisa bayar Rp0' },
    { key: 'order_ready_unpaid', label: 'Siap Diambil — Belum Lunas', desc: 'Saat status menjadi Siap Diambil dan masih ada sisa bayar' },
    { key: 'piutang_reminder', label: 'Tagihan Belum Lunas', desc: 'Pengingat piutang otomatis' },
    { key: 'ngendon_reminder', label: 'Pengingat Ngendon', desc: 'Pesanan selesai tapi belum diambil' },
    { key: 'dp_reminder', label: 'Reminder DP', desc: 'Pengingat sisa pembayaran harian' },
    { key: 'laporan_harian', label: 'Laporan Harian', desc: 'Laporan ke owner setiap hari' },
  ];
  const DEFAULT_TEMPLATE_TEXTS = {
    order_created: 'Halo {customer_name}! 👋\n\nPesanan Anda di *{store_name}* telah berhasil dibuat.\n\n🔖 Kode pesanan: *{tracking_code}*\n💰 Total: *{total_amount}*\n\nLacak status pesanan Anda di sini:\n{track_url}\n\nTerima kasih telah mempercayai kami!',
    status_update: 'Halo {customer_name}, pesanan Anda (*{tracking_code}*) telah diperbarui.\n\nStatus: *{status_label}*\n\nLacak pesanan Anda: {track_url}\n\nTerima kasih telah mempercayai {store_name}.',
    order_ready_paid: 'Halo {customer_name}, pesanan Anda sudah selesai dan *lunas*! 🎉\n\nKode pesanan: *{tracking_code}*\nTotal pembayaran: *{total_amount}*\nStatus pembayaran: *LUNAS* ✅\n\nSilakan segera diambil di {store_name}.\n\nDetail pesanan: {track_url}',
    order_ready_unpaid: 'Halo {customer_name}, pesanan Anda sudah selesai! 🎉\n\nKode pesanan: *{tracking_code}*\nTotal tagihan: *{total_amount}*\nSisa pembayaran: *{remaining_amount}*\n\nSilakan lakukan pelunasan melalui metode pembayaran di sini:\n{payment_url}\n\nSetelah melakukan pembayaran, silakan lakukan konfirmasi.\n\nSilakan segera diambil di {store_name}.',
    piutang_reminder: 'Halo {customer_name}, kami mengingatkan sisa tagihan pesanan Anda.\n\nKode pesanan: *{tracking_code}*\nSisa yang perlu dilunasi: *{remaining_amount}*\n\nMohon segera dilunasi. Terima kasih, {store_name}.',
    ngendon_reminder: 'Halo {customer_name}, pesanan Anda (*{tracking_code}*) sudah selesai *{days} hari yang lalu* namun belum diambil.\n\nDetail pesanan: {track_url}\n\nMohon segera diambil. Terima kasih, {store_name}.',
    dp_reminder: 'Halo {customer_name}, mengingatkan sisa pembayaran pesanan *{tracking_code}*.\n\nSisa: *{remaining_amount}*\n\nTerima kasih, {store_name}.',
    laporan_harian: '📊 *Laporan Harian {store_name}*\n📅 {date}\n\n💰 Omset: *{omset}*\n📦 Pesanan Masuk: *{pesanan_baru}*\n✅ Pesanan Selesai: *{pesanan_selesai}*\n💵 Kas Masuk: *{kas_masuk}*\n📋 Piutang: *{piutang}*',
  };

  if (isLoading || !form) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-48">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin" />
      </div>
    </AdminLayout>
  );

  const isOwner = user?.role === 'owner';

  const fields = [
    { key: 'name', label: t('Nama Toko', 'Store Name'), icon: Store, required: true },
    { key: 'phone', label: t('No. Telepon', 'Phone'), icon: Phone },
    { key: 'address', label: t('Alamat', 'Address'), icon: MapPin },
    { key: 'email', label: 'Email', icon: Mail, type: 'email' },
    { key: 'website', label: 'Website', icon: Globe },
  ];

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Pengaturan Toko', 'Store Settings')}</h1>
          <p className="text-slate-500 text-sm mt-0.5">{t('Kelola informasi dan pengaturan toko Anda', 'Manage your store information and settings')}</p>
        </div>

        {!isOwner && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
            {t('Hanya owner yang bisa mengubah pengaturan toko.', 'Only store owners can edit settings.')}
          </div>
        )}

        {/* Tab Bar */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 flex-wrap">
          {[
            { id: 'umum', label: t('Umum', 'General') },
            { id: 'wa', label: 'WhatsApp' },
            { id: 'pembayaran', label: t('Pembayaran', 'Payment') },
            { id: 'tampilan', label: t('Tampilan', 'Display') },
            { id: 'billing', label: 'Billing' },
          ].map(tab => (
            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
              data-testid={`settings-tab-${tab.id}`}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB: UMUM ─────────────────────────────────── */}
        {activeTab === 'umum' && (
          <>
            {/* Tipe Bisnis */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-slate-400" />
                <h3 className="font-semibold text-slate-800 font-manrope">{t('Tipe Bisnis', 'Business Type')}</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">{t('Mempengaruhi form pesanan, kolom kanban, dan fitur yang tersedia.', 'Affects order form, kanban columns, and available features.')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(BUSINESS_TYPES).map(([key, cfg]) => {
                  const selected = form.business_type === key;
                  return (
                    <button key={key} type="button" disabled={!isOwner}
                      onClick={() => setForm(f => ({ ...f, business_type: key, business_type_custom: key === 'lainnya' ? f.business_type_custom : '' }))}
                      className={`relative text-left p-3.5 rounded-xl border-2 transition-all ${
                        selected ? 'border-[#0f6c44] bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                      } ${!isOwner ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                      {selected && <CheckCircle size={15} className="absolute top-2.5 right-2.5 text-[#0f6c44]" />}
                      <span className="text-xl mb-1.5 block">{cfg.emoji}</span>
                      <p className={`text-xs font-bold leading-tight ${selected ? 'text-[#0f6c44]' : 'text-slate-700'}`}>{cfg.label}</p>
                    </button>
                  );
                })}
              </div>
              {form.business_type && BUSINESS_TYPES[form.business_type] && (
                <div className="mt-3 bg-emerald-50 rounded-xl px-4 py-3 text-xs text-[#0f6c44]">
                  <strong>{BUSINESS_TYPES[form.business_type].emoji} {BUSINESS_TYPES[form.business_type].label}</strong> — {BUSINESS_TYPES[form.business_type].description}
                </div>
              )}
              {form.business_type === 'lainnya' && <div className="mt-4"><label className="mb-1.5 block text-sm font-medium text-slate-700">Jenis Usaha Anda <span className="font-normal text-slate-400">(opsional)</span></label><input value={form.business_type_custom || ''} onChange={set('business_type_custom')} disabled={!isOwner} data-testid="settings-custom-business-type-input" placeholder="Contoh: Salon, les privat, cuci kendaraan" className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#0f6c44] disabled:opacity-60"/></div>}
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <label className="mb-1.5 block text-sm font-semibold text-slate-800">Target Omset Bulanan</label>
              <p className="mb-3 text-xs text-slate-500">Target ini dipakai untuk menghitung persentase pencapaian pada chatbot owner.</p>
              <input type="number" min="0" value={form.monthly_target || ''} onChange={event => setStoreValue('monthly_target', Number(event.target.value) || 0)} disabled={!isOwner} data-testid="monthly-target-input" placeholder="Contoh: 10000000" className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#0f6c44] disabled:cursor-not-allowed disabled:opacity-60" />
            </div>

            {/* Info Toko */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-5">
              <h3 className="font-semibold text-slate-800 font-manrope">{t('Informasi Toko', 'Store Information')}</h3>
              {fields.map(f => (
                <div key={f.key}>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-1.5">
                    <f.icon size={15} className="text-slate-400" />
                    {f.label}{f.required ? ' *' : ''}
                  </label>
                  <input type={f.type || 'text'} value={form[f.key] || ''} onChange={set(f.key)}
                    disabled={!isOwner} required={f.required}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] disabled:opacity-60 disabled:cursor-not-allowed" />
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-slate-800 font-manrope">Modul Inventory</h3>
                  <p className="mt-1 text-xs text-slate-500">Aktifkan untuk memotong stok otomatis pada produk bertipe Barang saat pesanan dibuat.</p>
                </div>
                <button type="button" onClick={() => setStoreValue('inventory_enabled', !form.inventory_enabled)} disabled={!isOwner} data-testid="toggle-inventory-enabled" aria-pressed={Boolean(form.inventory_enabled)} className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${form.inventory_enabled ? 'bg-[#0f6c44]' : 'bg-slate-300'} disabled:cursor-not-allowed disabled:opacity-50`}>
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${form.inventory_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            {/* Footer Nota */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-2 mb-4">
                <FileText size={16} className="text-slate-400" />
                <h3 className="font-semibold text-slate-800 font-manrope">{t('Footer Nota', 'Receipt Footer')}</h3>
              </div>
              <textarea value={form.receipt_footer || ''} onChange={set('receipt_footer')} disabled={!isOwner} rows={3}
                placeholder={t('Teks yang akan muncul di bagian bawah nota...', 'Text shown at the bottom of receipts...')}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] resize-none disabled:opacity-60" />
            </div>
          </>
        )}

        {/* ── TAB: WHATSAPP ─────────────────────────────── */}
        {activeTab === 'wa' && isOwner && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-5">
            <div className="flex items-center gap-2">
              <MessageCircle size={16} className="text-green-600" />
              <h3 className="font-semibold text-slate-800 font-manrope">Integrasi WhatsApp</h3>
            </div>
            <p className="text-xs text-slate-500 -mt-2">Pilih sumber pesan WhatsApp yang digunakan untuk notifikasi pelanggan.</p>

            <div className="grid grid-cols-2 gap-3">
              {[
                { val: 'kasirwa', icon: Shield, title: 'WA KasirWA.com', desc: 'Gratis, menggunakan nomor sistem kami' },
                { val: 'pribadi', icon: Smartphone, title: 'WA Pribadi Toko', desc: 'Gunakan token Fonnte milik toko sendiri' },
              ].map(opt => {
                const Icon = opt.icon;
                const selected = (wa.integration_type || 'kasirwa') === opt.val;
                return (
                  <button key={opt.val} type="button" onClick={() => setWa('integration_type')(opt.val)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${selected ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon size={16} className={selected ? 'text-green-600' : 'text-slate-400'} />
                      <span className={`text-sm font-bold ${selected ? 'text-green-700' : 'text-slate-700'}`}>{opt.title}</span>
                    </div>
                    <p className="text-xs text-slate-500">{opt.desc}</p>
                    {selected && <div className="mt-2 inline-flex items-center gap-1 text-xs text-green-600 font-semibold"><CheckCircle size={12} />Aktif</div>}
                  </button>
                );
              })}
            </div>

            {wa.integration_type === 'pribadi' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Token Fonnte Pribadi</label>
                <input type="text" value={wa.custom_token || ''} placeholder="Masukkan token Fonnte Anda..."
                  onChange={e => setWa('custom_token')(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:border-green-500" />
                <p className="text-xs text-slate-400 mt-1">Dapatkan token dari <a href="https://app.fonnte.com" target="_blank" rel="noreferrer" className="text-green-600 underline">app.fonnte.com</a></p>
              </div>
            )}

            <div className="bg-slate-50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-700">Test Koneksi WA</p>
              <div className="flex gap-2">
                <input value={testTarget} onChange={e => setTestTarget(e.target.value)} placeholder={form?.phone || '08xxxxxxxxxx'}
                  className="flex-1 px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-green-500" />
                <button onClick={() => testMutation.mutate()} disabled={testMutation.isPending}
                  data-testid="test-wa-button"
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-all disabled:opacity-70">
                  {testMutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : <><TestTube size={14} />Test</>}
                </button>
              </div>
            </div>

            {/* Notifikasi Pesanan */}
            <div className="border-t border-slate-100 pt-5 space-y-3">
              <div className="flex items-center gap-2">
                <Bell size={15} className="text-green-600" />
                <p className="text-sm font-semibold text-slate-700">Notifikasi Pesanan</p>
              </div>
              <p className="text-xs text-slate-400">WA dikirim otomatis atau hanya tampil tombol manual per tahap</p>
              {[
                { key: 'dibuat', label: 'Saat Dibuat', desc: 'Pesanan baru masuk' },
                { key: 'diproses', label: 'Diproses', desc: 'Status berubah ke proses' },
                { key: 'siap_diambil', label: 'Siap Diambil', desc: 'Pesanan siap / serah terima' },
                { key: 'selesai', label: 'Selesai', desc: 'Pesanan ditandai selesai' },
              ].map(evt => (
                <div key={evt.key} className="flex items-center justify-between py-1.5">
                  <div>
                    <p className="text-sm text-slate-700">{evt.label}</p>
                    <p className="text-xs text-slate-400">{evt.desc}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {['auto', 'manual'].map(opt => (
                      <button key={opt} type="button" onClick={() => setWaTrigger(evt.key)(opt)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                          (triggers[evt.key] ?? 'auto') === opt
                            ? opt === 'auto' ? 'bg-green-600 text-white border-green-600' : 'bg-slate-700 text-white border-slate-700'
                            : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                        }`}>
                        {opt === 'auto' ? 'Otomatis' : 'Manual'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Pengingat Otomatis */}
            <div className="border-t border-slate-100 pt-5 space-y-4">
              <p className="text-sm font-semibold text-slate-700">Pengingat Otomatis</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-700">Ngendon Reminder <span className="text-xs text-slate-400 font-normal">(pukul 09:00)</span></p>
                  <p className="text-xs text-slate-400">WA otomatis jika pesanan selesai belum diambil</p>
                </div>
                <div className="flex items-center gap-2">
                  {(wa.ngendon_reminder_enabled ?? true) && (
                    <>
                      <input type="number" min="1" max="30" value={wa.ngendon_days || 3}
                        onChange={e => setWa('ngendon_days')(parseInt(e.target.value))}
                        className="w-14 px-2 py-1.5 text-center text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none" />
                      <span className="text-xs text-slate-500">hari</span>
                    </>
                  )}
                  <button type="button" onClick={() => setWa('ngendon_reminder_enabled')(!(wa.ngendon_reminder_enabled ?? true))}
                    className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${(wa.ngendon_reminder_enabled ?? true) ? 'bg-green-500' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${(wa.ngendon_reminder_enabled ?? true) ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-700">DP Reminder <span className="text-xs text-slate-400 font-normal">(pukul 10:00)</span></p>
                  <p className="text-xs text-slate-400">WA otomatis untuk pesanan yang belum lunas</p>
                </div>
                <button type="button" onClick={() => setWa('dp_reminder_enabled')(!(wa.dp_reminder_enabled ?? true))}
                  className={`w-12 h-6 rounded-full transition-colors relative ${(wa.dp_reminder_enabled ?? true) ? 'bg-green-500' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${(wa.dp_reminder_enabled ?? true) ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-700">Laporan Harian Otomatis</p>
                  <p className="text-xs text-slate-400">Ringkasan omset dikirim ke nomor toko</p>
                </div>
                <button type="button" onClick={() => setWa('laporan_harian_enabled')(!wa.laporan_harian_enabled)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${wa.laporan_harian_enabled ? 'bg-green-500' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${wa.laporan_harian_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {wa.laporan_harian_enabled && (
                <div className="flex items-center gap-3 pl-4">
                  <label className="text-sm text-slate-600">Jam kirim:</label>
                  <input type="time" value={wa.laporan_harian_time || '20:00'}
                    onChange={e => setWa('laporan_harian_time')(e.target.value)}
                    className="px-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none" />
                  <button onClick={() => waApi.sendDailyReport().then(result => result.data.simulated ? toast.info(result.data.message) : toast.success('Laporan dikirim!')).catch(err => toast.error(formatApiError(err)))}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-200 transition-colors">
                    <Send size={12} />Kirim Sekarang
                  </button>
                </div>
              )}
            </div>

            {/* Template editor */}
            <div className="border-t border-slate-100 pt-4">
              <button type="button" onClick={() => setShowTemplates(v => !v)}
                className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-slate-900">
                Template Pesan
                {showTemplates ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showTemplates && (
                <div className="mt-4 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {TEMPLATE_KEYS.map(tk => (
                      <button key={tk.key} type="button" onClick={() => setActiveTemplate(tk.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeTemplate === tk.key ? 'bg-green-100 text-green-700 ring-1 ring-green-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                        {tk.key === 'order_created' && '⭐ '}{tk.label}
                      </button>
                    ))}
                  </div>
                  {/* Active template description */}
                  {TEMPLATE_KEYS.find(tk => tk.key === activeTemplate)?.desc && (
                    <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                      <span className="font-semibold">Digunakan:</span> {TEMPLATE_KEYS.find(tk => tk.key === activeTemplate).desc}
                    </p>
                  )}
                  <textarea rows={7}
                    value={(wa.templates || {})[activeTemplate] || DEFAULT_TEMPLATE_TEXTS[activeTemplate] || ''}
                    onChange={e => setForm(f => ({ ...f, wa_settings: { ...(f.wa_settings || {}), templates: { ...((f.wa_settings || {}).templates || {}), [activeTemplate]: e.target.value } } }))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:border-green-500 resize-none" />
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Variabel yang bisa dipakai:{' '}
                    {['{customer_name}','{tracking_code}','{track_url}','{payment_url}','{status_label}','{total_amount}','{remaining_amount}','{store_name}'].map(v => (
                      <code key={v} className="bg-slate-100 px-1 rounded mr-1">{v}</code>
                    ))}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === 'wa' && !isOwner && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-700">
            {t('Hanya owner yang bisa mengatur integrasi WhatsApp.', 'Only store owners can configure WhatsApp integration.')}
          </div>
        )}

        {/* ── TAB: PEMBAYARAN ──────────────────────────── */}
        {activeTab === 'pembayaran' && isOwner && (
          <div className="space-y-5">
            {/* Layout 2 kolom: Manual kiri, Otomatis kanan */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Pembayaran Manual */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard size={16} className="text-slate-400" />
                    <h3 className="font-semibold text-slate-800 font-manrope">{t('Pembayaran Manual', 'Manual Payment')}</h3>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-xs text-emerald-700 space-y-1 mb-3">
                    <p className="font-semibold text-emerald-800">{t('Cocok untuk UMKM / Usaha Rintisan', 'Best for Small/Startup Business')}</p>
                    <p><span className="font-semibold">+ {t('Kelebihan', 'Pros')}:</span> {t('Tanpa biaya, uang langsung masuk', 'No fees, money received directly')}</p>
                    <p><span className="font-semibold">- {t('Kekurangan', 'Cons')}:</span> {t('Harus konfirmasi manual', 'Manual confirmation required')}</p>
                  </div>
                  <p className="text-xs text-slate-500">
                    {t('Aktifkan metode yang tersedia di toko Anda.', 'Active methods will appear on the customer tracking page.')}
                  </p>
                </div>

                <div className="space-y-3">
                  {PAYMENT_METHODS.map(method => {
                    const config = pm[method.key] || {};
                    const isEnabled = config.enabled || false;
                    return (
                      <div key={method.key} data-testid={`payment-method-${method.key}`}
                        className={`border rounded-xl overflow-hidden transition-all ${isEnabled ? 'border-emerald-200' : 'border-slate-200'}`}>
                        <div className="flex items-center justify-between p-3.5">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-extrabold ${method.color}`}>
                              {method.key === 'shopee_pay' ? 'SPy' : method.label.slice(0, 3).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{method.label}</p>
                              <p className="text-xs text-slate-400">{method.subtitle}</p>
                            </div>
                          </div>
                          <button type="button" data-testid={`toggle-${method.key}`}
                            onClick={() => setPm(method.key, 'enabled', !isEnabled)}
                            className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${isEnabled ? 'bg-green-500' : 'bg-slate-300'}`}>
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                        {isEnabled && method.type !== 'qris' && (
                          <div className="px-3.5 pb-3.5 bg-slate-50/60 border-t border-slate-100 space-y-2.5 pt-2.5">
                            <input value={config.account_number || ''} onChange={e => setPm(method.key, 'account_number', e.target.value)}
                              placeholder={method.type === 'ewallet' ? 'No. HP / Akun' : 'No. Rekening'}
                              data-testid={`input-account-number-${method.key}`}
                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-green-500" />
                            <input value={config.account_name || ''} onChange={e => setPm(method.key, 'account_name', e.target.value)}
                              placeholder="Nama Pemilik"
                              data-testid={`input-account-name-${method.key}`}
                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-green-500" />
                          </div>
                        )}
                        {isEnabled && method.type === 'qris' && (
                          <div className="px-3.5 pb-3.5 bg-slate-50/60 border-t border-slate-100 pt-2.5">
                            {config.qris_image_url ? (
                              <div className="flex items-start gap-3 mt-1">
                                <img src={config.qris_image_url} alt="QRIS" className="w-24 h-24 object-contain border border-slate-200 rounded-xl bg-white p-1" />
                                <div className="space-y-1.5 pt-1">
                                  <p className="text-xs text-green-700 font-semibold">QRIS sudah diupload ✓</p>
                                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5">
                                    <input type="file" accept="image/*" className="hidden" onChange={handleQrisUpload} />
                                    {t('Ganti', 'Change')}
                                  </label>
                                  <button type="button" onClick={() => setPm('qris', 'qris_image_url', '')} className="block text-xs text-red-500 hover:text-red-700">{t('Hapus', 'Remove')}</button>
                                </div>
                              </div>
                            ) : (
                              <label className={`flex flex-col items-center gap-1.5 border-2 border-dashed rounded-xl p-4 cursor-pointer transition-colors ${qrisUploading ? 'border-green-300 bg-green-50/50' : 'border-slate-300 hover:border-green-400'}`}>
                                <input type="file" accept="image/*" className="hidden" onChange={handleQrisUpload} disabled={qrisUploading} />
                                {qrisUploading ? <div className="w-5 h-5 border-2 border-slate-200 border-t-green-500 rounded-full animate-spin" /> : <QrCode size={24} className="text-slate-300" />}
                                <p className="text-xs font-medium text-slate-500">{qrisUploading ? 'Mengupload...' : 'Upload QRIS'}</p>
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pembayaran Otomatis - Coming Soon */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4 relative overflow-hidden opacity-70">
                {/* Overlay coming soon */}
                <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex flex-col items-center justify-center gap-2 rounded-2xl">
                  <div className="bg-slate-800 text-white px-4 py-1.5 rounded-full text-sm font-bold tracking-wide">Coming Soon</div>
                  <p className="text-xs text-slate-500 text-center px-6">Fitur ini sedang dalam pengembangan dan akan segera tersedia</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard size={16} className="text-slate-400" />
                    <h3 className="font-semibold text-slate-800 font-manrope">{t('Pembayaran Otomatis', 'Automatic Payment')}</h3>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">via Xendit</span>
                  </div>
                  <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 text-xs text-purple-700 space-y-1 mb-3">
                    <p className="font-semibold text-purple-800">{t('Cocok untuk Usaha Stabil / Berkembang', 'Best for Established Business')}</p>
                    <p><span className="font-semibold">+ {t('Kelebihan', 'Pros')}:</span> {t('Konfirmasi otomatis, tidak perlu cek manual', 'Auto confirmation, no manual checking')}</p>
                    <p><span className="font-semibold">- {t('Kekurangan', 'Cons')}:</span> {t('Terdapat biaya transaksi per pembayaran', 'Transaction fee per payment applies')}</p>
                  </div>
                  <p className="text-xs text-slate-500">{t('Pelanggan bisa bayar langsung dari halaman lacak pesanan.', 'Customers can pay directly from the tracking page.')}</p>
                </div>
                <div className="space-y-3 pointer-events-none">
                  {['Transfer Bank (VA)', 'QRIS', 'GoPay / OVO / Dana', 'Kartu Kredit'].map(m => (
                    <div key={m} className="border border-slate-200 rounded-xl p-3 flex items-center justify-between">
                      <p className="text-sm text-slate-600">{m}</p>
                      <div className="w-10 h-5 rounded-full bg-slate-200" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'pembayaran' && !isOwner && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-700">
            {t('Hanya owner yang bisa mengatur metode pembayaran.', 'Only store owners can configure payment methods.')}
          </div>
        )}

        {/* ── TAB: TAMPILAN ─────────────────────────────── */}
        {activeTab === 'tampilan' && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4">
            <h3 className="font-semibold text-slate-800 font-manrope">{t('Pengaturan Tampilan', 'Display Settings')}</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">{t('Bahasa Antarmuka', 'Interface Language')}</p>
                <p className="text-xs text-slate-400 mt-0.5">{t('Pilih bahasa tampilan aplikasi', 'Choose application display language')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-slate-400" />
                <span className="text-sm text-slate-500">{t('Ganti di sidebar ↙', 'Toggle in sidebar ↙')}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: BILLING ─────────────────────────────── */}
        {activeTab === 'billing' && (
          <div className="space-y-4">
          <div data-testid="independence-promo-status" className="border border-amber-200 bg-amber-50 rounded-2xl p-5 flex items-start gap-4">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-xl">🇮🇩</div>
            <div><p className="text-sm font-bold text-amber-900">Promo Kemerdekaan — Aktif</p><p className="mt-1 text-sm text-amber-800">Paket Pro Automation gratis selama 3 bulan pertama saat promo diklaim.</p></div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] text-center space-y-3">
            <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto">
              <Shield size={28} className="text-[#0f6c44]" />
            </div>
            <h3 className="font-bold text-slate-800 font-manrope">{t('Paket & Billing', 'Plan & Billing')}</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto">{t('Fitur billing akan segera tersedia. Saat ini Anda menggunakan paket gratis.', 'Billing features coming soon. You are currently on the free plan.')}</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-[#0f6c44] rounded-xl text-sm font-semibold">
              <CheckCircle size={16} />Paket Gratis — Aktif
            </div>
          </div>
          </div>
        )}

        {isOwner && (
          <button onClick={() => mutation.mutate(form)} disabled={mutation.isPending}
            data-testid="save-settings-button"
            className="w-full h-12 bg-[#0f6c44] text-white rounded-xl font-semibold hover:bg-[#0c5636] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-70 shadow-sm">
            {mutation.isPending ? <Spinner size="sm" className="border-white/30 border-t-white" /> : <><Save size={18} />{t('Simpan Pengaturan', 'Save Settings')}</>}
          </button>
        )}
      </div>
    </AdminLayout>
  );
}
